import React, { useEffect, useRef, useState } from "react";
import styles from "./Notification.module.scss";
import classnames from "classnames";
import { ReactComponent as ConfirmIcon } from "../../images/confirm-icon.svg";
import { ReactComponent as WarningIcon } from "../../images/warning-icon.svg";
import { ReactComponent as ErrorIcon } from ".../../images/error-icon.svg";
import { ReactComponent as CloseIcon } from "../../images/close-icon.svg";

interface Props {
    header: string;
    text: string;
    type: "success" | "warning" | "error";
    onClose?: () => void;
    closeAfterMs?: number; 
}

const renderIcon = (type: Props["type"]) => {
    switch (type) {
        case "success":
            return <ConfirmIcon />;
        case "warning":
            return <WarningIcon />;
        case "error":
            return <ErrorIcon />;

        default:
            return <ConfirmIcon />;
    }
};

const Notification = ({ header, text, type = "success", onClose, closeAfterMs = 3000 }: Props) => {
    // please, keep this value in sync with css animation duration
    const animationDurationMs = 500;

    const [hideNotification, setHideNotification] = useState(false);

    const closeNotificationTimeoutId = useRef<NodeJS.Timeout | undefined>(
        undefined
    );
    const onHideCallbackTimeoutId = useRef<NodeJS.Timeout | undefined>(
        undefined
    );
    const isCloseButtonClicked = useRef<boolean>(false);

    const wrapperClassnames = classnames(
        styles.wrapper,
        styles[`wrapper--${type}`],
        { [styles[`wrapper--show`]]: !hideNotification },
        { [styles[`wrapper--hide`]]: hideNotification }
    );

    const handleKeyPress = (evt: React.KeyboardEvent) => {
        evt.key === "Enter" && handleOnClose();
    };

    useEffect(() => {
        setTimeout(() => {
            addClosingTimeouts();
        }, animationDurationMs);

        return () => {
            if (closeNotificationTimeoutId.current) {
                clearTimeout(closeNotificationTimeoutId.current);
            }

            if (onHideCallbackTimeoutId.current) {
                clearTimeout(onHideCallbackTimeoutId.current);
            }
        };
    }, []);

    const clearClosingTimeout = () => {
        if (closeNotificationTimeoutId.current) {
            clearTimeout(closeNotificationTimeoutId.current);
        }
    };

    const addClosingTimeouts = () => {
        closeNotificationTimeoutId.current = setTimeout(() => {
            handleOnClose();
        }, closeAfterMs);
    };

    const handleOnClose = () => {
        setHideNotification(true);
        invokeCallbackOnAnimationComplete(onClose);
    };

    const invokeCallbackOnAnimationComplete = (
        callback: (() => void) | undefined
    ) => {
        onHideCallbackTimeoutId.current = setTimeout(() => {
            callback?.();
        }, animationDurationMs);
    };

    return (
        <div
            onMouseEnter={clearClosingTimeout}
            onMouseLeave={() => {
                // the event is weirdly triggered when the close button is clicked, so don't do anything
                if (isCloseButtonClicked.current) {
                    return;
                }

                addClosingTimeouts();
            }}
            className={wrapperClassnames}
        >
            <div
                tabIndex={0}
                role="button"
                className={styles.crossIcon}
                onKeyPress={handleKeyPress}
                onClick={() => {
                    isCloseButtonClicked.current = true;
                    handleOnClose();
                }}
            >
                <CloseIcon />
            </div>

            <div className={styles.icon}>{renderIcon(type)}</div>

            <div className={styles.content}>
                <h3 className={styles.header}>{header}</h3>

                <div className={styles.text}>{text}</div>
            </div>
        </div>
    );
};

export default Notification;
